import React, { useState } from 'react';
import { UserCardView, UserCardViewProps } from './UserCard.view';
import {
  UserInvitationInput,
  UserOrganizationAssignmentInput,
  UserOrganizationRole,
  ResendUserInvitationMutationFn,
  RemoveUserWorkspaceAssignmentMutationFn,
  CreateUserWorkspaceAssignmentMutationFn,
  UpdateUserOrganizationAssignmentMutationFn,
  UpdateUserInvitationMutationFn
} from '../../../models/graphql';
import { useUserRole } from '../../../hooks/useUserRole';
import { useUserData } from '../../../hooks/useUserData';
import { RegisterOptions } from 'react-hook-form';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { ApolloError } from '@apollo/client';
import { SettingsTeamMemberStatusEnum } from '../../../constants/team-members';
import { showToast } from '../../../helpers/showToast';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';

export type UserCardControllerProps = Omit<
  UserCardViewProps,
  | 'isSelf'
  | 'isInvitedUser'
  | 'selectedOrgRole'
  | 'isDotMenuEnabled'
  | 'removeUserMenuLabel'
  | 'openModal'
  | 'setOpenModal'
  | 'rules'
  | 'workspaceAssignmentToggleAction'
  | 'userRoleChangeHandler'
  | 'resendUserInvitationHandler'
  | 'assignedWorkspaceIds'
> & {
  updateUserInvitation: UpdateUserInvitationMutationFn;
  updateUserOrganizationAssignment: UpdateUserOrganizationAssignmentMutationFn;
  createUserWorkspaceAssignment: CreateUserWorkspaceAssignmentMutationFn;
  removeUserWorkspaceAssignment: RemoveUserWorkspaceAssignmentMutationFn;
  resendUserInvitation: ResendUserInvitationMutationFn;
};

export const UserCardController = (props: UserCardControllerProps): JSX.Element => {
  const { userData } = useUserData();
  const { selectedOrgRole } = useUserRole();
  const { selectedOrganization } = useSelectedOrganization();
  const rules: RegisterOptions = {};

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [assignedWorkspaceIds, setAssignedWorkspaceIds] = useState<Array<string>>(props.user.workspaces || []);

  const isInvitedUser = props.user.status === SettingsTeamMemberStatusEnum.INVITED;
  const isSelf = !isInvitedUser && userData?.settings.id === props.user.id;
  const isDotMenuEnabled =
    props.user.role === UserOrganizationRole.EDITOR ||
    (props.user.role === UserOrganizationRole.ADMIN && selectedOrgRole === UserOrganizationRole.OWNER);

  const removeUserMenuLabel = isInvitedUser ? 'Revoke Invite' : 'Remove from Organization';

  const workspaceAssignmentToggleAction = (
    newVal: boolean,
    workspaceId: string,
    complete: (keepNewVal: boolean | undefined) => void
  ) => {
    if (props.user.status === SettingsTeamMemberStatusEnum.MEMBER) {
      // If Workspace is being Assigned for a User
      if (newVal) {
        props
          .createUserWorkspaceAssignment({
            variables: { workspaceId: workspaceId, userId: props.user.id }
          })
          .then(() => {
            complete(true);
            setAssignedWorkspaceIds([...assignedWorkspaceIds, workspaceId]);
          })
          .catch(e => {
            const errorForRemapping = e as ApolloError;
            const mappedError = remapApolloErrors(errorForRemapping);
            showToast({
              summary: 'Error adding user to workspace',
              detail: mappedError,
              severity: 'error'
            });
            complete(false);
          });
      } else {
        // If Workspace is being Un-Assigned for a User
        props
          .removeUserWorkspaceAssignment({
            variables: { workspaceId: workspaceId, userId: props.user.id }
          })
          .then(() => {
            complete(true);
            setAssignedWorkspaceIds(assignedWorkspaceIds.filter(awId => awId !== workspaceId));
          })
          .catch(e => {
            const errorForRemapping = e as ApolloError;
            const mappedError = remapApolloErrors(errorForRemapping);
            showToast({
              summary: 'Error removing user from workspace',
              detail: mappedError,
              severity: 'error'
            });
            complete(false);
          });
      }
    } else {
      let newAssignedWorkspaceIds: string[] = [];
      newVal
        ? // If Workspace is being Assigned for an Invited User
          (newAssignedWorkspaceIds = [...assignedWorkspaceIds, workspaceId])
        : // If Workspace is being Un-Assigned for an Invited User
          (newAssignedWorkspaceIds = assignedWorkspaceIds.filter(awId => awId !== workspaceId));

      setAssignedWorkspaceIds(() => newAssignedWorkspaceIds);

      const $input: UserInvitationInput = {
        email: props.user.email,
        role: props.user.role,
        workspaceIds: newAssignedWorkspaceIds
      };

      props
        .updateUserInvitation({
          variables: { input: $input, userInvitationId: props.user.id }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error updating invited user workspaces',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    }
  };

  const userRoleChangeHandler = (role: UserOrganizationRole, complete: (keepNewVal: boolean | undefined) => void) => {
    if (selectedOrganization && props.user.status !== SettingsTeamMemberStatusEnum.INVITED) {
      const $input: UserOrganizationAssignmentInput = {
        role: role
      };

      props
        .updateUserOrganizationAssignment({
          variables: { input: $input, organizationId: selectedOrganization.id, userId: props.user.id }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error changing user role',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else if (selectedOrganization && props.user.status === SettingsTeamMemberStatusEnum.INVITED) {
      const $input: UserInvitationInput = {
        email: props.user.email,
        role: role,
        workspaceIds: props.user.workspaces
      };

      props
        .updateUserInvitation({
          variables: { input: $input, userInvitationId: props.user.id }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error updating Invited user role',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    }
  };

  const resendUserInvitationHandler = (invitationId: string) => {
    props
      .resendUserInvitation({
        variables: { userInvitationId: invitationId }
      })
      .then(() => {
        showToast({
          summary: 'Invitation resent',
          detail: 'An invitation has been resent to the invited user',
          severity: 'success'
        });
      })
      .catch(e => {
        const errorForRemapping = e as ApolloError;
        const mappedError = remapApolloErrors(errorForRemapping);
        showToast({
          summary: 'Error sending Invitation',
          detail: mappedError,
          severity: 'error'
        });
      });
  };

  const viewProps: UserCardViewProps = {
    ...props,
    assignedWorkspaceIds,
    isSelf,
    isInvitedUser,
    selectedOrgRole,
    isDotMenuEnabled,
    removeUserMenuLabel,
    openModal,
    setOpenModal,
    rules,
    workspaceAssignmentToggleAction,
    userRoleChangeHandler,
    resendUserInvitationHandler
  };

  return <UserCardView {...viewProps} />;
};
